import { Alert, App, Modal } from "antd"
import React, { useState } from "react"
import { useTranslation } from "react-i18next"

import { pendingCommitmentsFrom } from "../api/pending-commitments.ts"
import { archiveProject } from "../api/services.ts"
import type { ArchiveQuestion } from "../api/services.types.ts"
import { isArchivingStatus } from "../constants/project-statuses.ts"
import { PROJECTS_SAVE_RESULT } from "../constants/projects-save-results.ts"
import { archiveQuestionLabels } from "../helpers/archive-question-labels.ts"
import { PendingCommitmentsAlert } from "./PendingCommitmentsAlert.tsx"

interface ArchiveProjectModalProps {
  projectId: string
  projectName?: string | null
  // Undefined only for a caller that hasn't loaded the row yet — treated as
  // "unknown, don't block" rather than "not archivable", so the modal never
  // opens pre-blocked on a still-loading status.
  projectStatus?: string | number | null
  open: boolean
  onCancel: () => void
  onArchived?: () => void
}

export function ArchiveProjectModal({
  projectId,
  projectName,
  projectStatus,
  open,
  onCancel,
  onArchived
}: ArchiveProjectModalProps) {
  const { t } = useTranslation()
  const { message } = App.useApp()
  const [saving, setSaving] = useState(false)
  // Set when the server refuses because the project still has meetings booked
  // or signature requests out.
  const [question, setQuestion] = useState<ArchiveQuestion | null>(null)

  const archive = async (acknowledged: boolean) => {
    setSaving(true)
    const response = await archiveProject({
      id: projectId,
      ...(acknowledged ? { cancel_appointments: true } : {})
    })
    setSaving(false)

    if (response.ok) {
      setQuestion(null)
      message.success(t("projectArchive.success"))
      onArchived?.()
      return
    }

    const details = response.data as
      | { type?: string; message?: string; detail?: string }
      | undefined

    if (details?.type !== PROJECTS_SAVE_RESULT.PENDING_COMMITMENTS) {
      // Leaving confirm mode up would pin the modal to a list that no longer
      // describes the state and keep re-sending the confirmation.
      setQuestion(null)
      message.error(details?.message || details?.detail || t("projectArchive.failed"))
      return
    }

    const asked = pendingCommitmentsFrom(details)
    if (!asked) {
      setQuestion(null)
      message.warning(
        details.message || t("projectArchive.commitments.unavailable")
      )
      return
    }

    setQuestion(asked)
  }

  const displayName = projectName?.trim() || t("projectArchive.thisProject")
  const labels = question ? archiveQuestionLabels(question, "projectArchive", t) : null
  // Mirrors the server's own precondition (apps/core/project_archive.py,
  // reason_for_status) so the dialog states it up front instead of the user
  // confirming and hitting a surprise 400. `== null` covers both undefined
  // (not loaded yet) and null (genuinely unknown) without blocking either.
  const statusBlocked =
    projectStatus != null && !isArchivingStatus(projectStatus)

  return (
    <Modal
      open={open}
      title={t("projectArchive.title", { name: displayName })}
      okText={labels ? labels.confirm : t("projectArchive.confirm")}
      cancelText={
        labels
          ? t("projectArchive.commitments.keepActive")
          : t("projectArchive.cancel")
      }
      okButtonProps={{ danger: true, disabled: statusBlocked }}
      confirmLoading={saving}
      cancelButtonProps={{ disabled: saving }}
      closable={!saving}
      keyboard={!saving}
      maskClosable={false}
      onOk={() => {
        if (statusBlocked) return
        return archive(!!question)
      }}
      onCancel={onCancel}
      destroyOnHidden
    >
      {statusBlocked ? (
        <Alert
          type="error"
          showIcon
          className="mb-4"
          message={t("projectArchive.statusBlocked")}
        />
      ) : (
        <Alert
          type="warning"
          showIcon
          className="mb-4"
          message={t("projectArchive.impact")}
        />
      )}

      {question && (
        <PendingCommitmentsAlert
          commitments={question.pendingCommitments}
          signatures={question.pendingSignatures}
        />
      )}

    </Modal>
  )
}
